import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { chartConfigSelector } from "@store/slices/chart-config-slice";
import { getTimePeriodTitleSuffix } from "@utils/time-period-display";
import { getRollingAverageTitleSuffix } from "@utils/rolling-average-utils";

export function useBarChartDerivedState(
  isRefetchingData: boolean,
  dataAvailabilityDate?: string | null,
) {
  const { currentConfig } = useAppSelector(chartConfigSelector);
  const questions = useAppSelector((state) => state.chartConfig.question);

  const allVariables = useMemo(
    () =>
      questions
        ? [
            ...(questions.rowVariables || []),
            ...(questions.columnVariables || []),
          ]
        : [],
    [questions],
  );

  const allValues = useMemo(() => questions?.values || [], [questions]);

  const isAvailableData =
    !currentConfig.series ||
    currentConfig.series.length === 0 ||
    currentConfig.series.every((s) => s.data.length === 0);

  const canSave = !isAvailableData && !isRefetchingData;

  const hasSampleSizeBelowThreshold =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series &&
    currentConfig.series.length > 0 &&
    currentConfig.series.some(
      (series) =>
        (series as any).baseN !== undefined &&
        (series as any).baseN !== null &&
        (series as any).baseN <
          currentConfig.statisticalTesting.minimumSampleSize,
    );

  // Check if rolling average has partial historical data (less than requested window)
  // This happens when: rollingAverage is enabled AND effectiveRollingPeriodCount is less than windowSize
  const hasPartialHistoricalData =
    currentConfig.rollingAverage?.enabled &&
    currentConfig.statisticalTesting?.enabled &&
    (currentConfig as any).effectiveRollingPeriodCount !== undefined &&
    currentConfig.rollingAverage?.windowSize !== undefined &&
    (currentConfig as any).effectiveRollingPeriodCount > 0 &&
    (currentConfig as any).effectiveRollingPeriodCount <
      currentConfig.rollingAverage.windowSize;

  // Check if statistical testing is enabled but no previous period/rolling window data exists
  // Bar chart series have statSignificance property when stat testing data is available
  const hasNoPreviousPeriodData =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series &&
    currentConfig.series.length > 0 &&
    currentConfig.series.every(
      (series) =>
        (series as any).statSignificance === undefined ||
        (series as any).statSignificance === null,
    );

  const isAllTimePeriod =
    currentConfig.timePeriodSelection?.timePeriod === "all_time";

  const headerTitle = useMemo(() => {
    if (!questions?.name) return "Configure your bar chart";

    let baseTitle = questions.name;

    const hasCustomTitle =
      currentConfig.title !== "" &&
      currentConfig.title !== "Brand Tracking" &&
      questions &&
      currentConfig.title !== questions.name;

    // If title is customized, show the custom title
    if (hasCustomTitle) {
      return currentConfig.title;
    }

    if (currentConfig.focusEntityDisplayLabel) {
      baseTitle = `${baseTitle}  (${currentConfig.focusEntityDisplayLabel})`;
    }

    const rollingSuffix =
      currentConfig.rollingAverage?.enabled && currentConfig.rollingAverage?.windowSize
        ? getRollingAverageTitleSuffix(currentConfig.rollingAverage.windowSize)
        : "";

    if (currentConfig.timePeriodSelection) {
      const tp = currentConfig.timePeriodSelection.timePeriod;
      // For named periods, skip stale saved dates and re-resolve from current
      // dataset availability so the drawer title stays in sync with the layout.
      const isNamedPeriod =
        tp === "latest_quarter" ||
        tp === "previous_quarter" ||
        tp === "latest_semester" ||
        tp === "previous_semester";
      const timePeriodSuffix = getTimePeriodTitleSuffix(
        tp,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.startDate,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.endDate,
        dataAvailabilityDate ?? undefined,
      );
      if (timePeriodSuffix)
        return `${baseTitle}${timePeriodSuffix}${rollingSuffix}`;
    }

    return `${baseTitle}${rollingSuffix}`;
  }, [
    questions,
    currentConfig.title,
    currentConfig.focusEntityDisplayLabel,
    currentConfig.statisticalTesting?.enabled,
    currentConfig.timePeriodSelection,
    currentConfig.rollingAverage,
    currentConfig.effectiveRollingPeriodCount,
    dataAvailabilityDate,
  ]);

  return {
    questions,
    allVariables,
    allValues,
    isAvailableData,
    canSave,
    hasSampleSizeBelowThreshold,
    hasPartialHistoricalData,
    hasNoPreviousPeriodData,
    headerTitle,
    isAllTimePeriod,
  };
}
